import { useId, useRef, useState, type ReactNode } from 'react';
import { Button, cn, Field, Input, Select } from '@/core/ui';
import { Plus } from '@/core/ui/icons';
import {
  addVariable,
  isSystemVariable,
  SYSTEM_VARIABLES,
  toVariableName,
  variableNameProblem,
} from '../../domain/variables';
import { useBuilder } from './context';

export function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-2xl bg-surface-2 p-3.5">
      {title && <h3 className="text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">{title}</h3>}
      {children}
    </section>
  );
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  maxLength = 300,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  maxLength?: number;
  type?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      {(p) => (
        <Input
          {...p}
          type={type}
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Field>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 text-sm text-fg">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 rounded accent-teal-500"
      />
      <span>
        {label}
        {hint && <span className="block text-xs text-fg-subtle">{hint}</span>}
      </span>
    </label>
  );
}

/** Número opcional: vazio é `null`. Aceita vírgula decimal. */
export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step,
  hint,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  hint?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      {(p) => (
        <Input
          {...p}
          type="number"
          inputMode="decimal"
          value={value ?? ''}
          min={min}
          max={max}
          step={step ?? 'any'}
          onChange={(e) => {
            const raw = e.target.value.replace(',', '.');
            const n = Number(raw);
            onChange(raw === '' || Number.isNaN(n) ? null : n);
          }}
        />
      )}
    </Field>
  );
}

/** O visual dos campos do kit (`core/ui/Field`), para os que precisam de `ref`. */
const CONTROL =
  'block w-full rounded-lg border border-line-strong bg-surface px-3 py-2 text-sm text-fg placeholder:text-gray-400 focus:ring-2 focus:ring-teal-300 focus:outline-none';

/**
 * Texto que aceita `{{variáveis}}`: abaixo, as variáveis disponíveis —
 * clicar insere no ponto do cursor.
 */
export function TemplateField({
  label,
  value,
  onChange,
  placeholder,
  multiline,
  rows = 4,
  maxLength = 4000,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  hint?: string;
}) {
  const { doc } = useBuilder();
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const names = [...SYSTEM_VARIABLES.map((v) => v.name), ...doc.variables.map((v) => v.name)];

  const insert = (name: string) => {
    const el = ref.current;
    const token = `{{${name}}}`;
    const at = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? at;
    onChange(value.slice(0, at) + token + value.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + token.length, at + token.length);
    });
  };

  return (
    <Field label={label} hint={hint}>
      {(p) => (
        <div className="space-y-1.5">
          {multiline ? (
            <textarea
              {...p}
              ref={ref}
              rows={rows}
              value={value}
              maxLength={maxLength}
              placeholder={placeholder}
              onChange={(e) => onChange(e.target.value)}
              className={cn(CONTROL, 'resize-y')}
            />
          ) : (
            <input
              {...p}
              ref={ref}
              value={value}
              maxLength={maxLength}
              placeholder={placeholder}
              onChange={(e) => onChange(e.target.value)}
              className={cn(CONTROL, 'h-10')}
            />
          )}
          <div className="flex flex-wrap items-center gap-1" aria-label="Variáveis disponíveis">
            <span className="text-[11px] text-fg-subtle">Inserir:</span>
            {names.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => insert(n)}
                className="rounded-full bg-surface px-2 py-0.5 font-mono text-[11px] text-fg-muted ring-1 ring-line-strong hover:text-fg"
              >
                {`{{${n}}}`}
              </button>
            ))}
          </div>
        </div>
      )}
    </Field>
  );
}

const NEW = '__nova__';

/**
 * Escolha de variável: as declaradas (e, para leitura, as do sistema) e
 * "+ Nova variável…", que cria uma ali mesmo.
 */
export function VariableSelect({
  label,
  value,
  onChange,
  readable,
  hint,
  emptyLabel = 'Escolha…',
}: {
  label: string;
  value: string;
  onChange: (name: string) => void;
  /** Só lê a variável: as do sistema também servem. */
  readable?: boolean;
  hint?: string;
  emptyLabel?: string;
}) {
  const { doc } = useBuilder();
  const [creating, setCreating] = useState(false);
  const declared = doc.variables.map((v) => v.name);
  const unknown = value && !declared.includes(value) && !(readable && isSystemVariable(value));

  if (creating)
    return (
      <NewVariable
        label={label}
        onCancel={() => setCreating(false)}
        onCreate={(name) => {
          setCreating(false);
          onChange(name);
        }}
      />
    );

  return (
    <Field label={label} hint={hint}>
      {(p) => (
        <Select
          {...p}
          value={value}
          onChange={(e) => (e.target.value === NEW ? setCreating(true) : onChange(e.target.value))}
        >
          <option value="">{emptyLabel}</option>
          {unknown && <option value={value}>{value} (não declarada)</option>}
          {declared.length > 0 && (
            <optgroup label="Variáveis do fluxo">
              {declared.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </optgroup>
          )}
          {readable && (
            <optgroup label="Da pessoa (automáticas)">
              {SYSTEM_VARIABLES.map((v) => (
                <option key={v.name} value={v.name}>
                  {v.name} — {v.label}
                </option>
              ))}
            </optgroup>
          )}
          <option value={NEW}>+ Nova variável…</option>
        </Select>
      )}
    </Field>
  );
}

/** Campo para nomear e declarar uma variável nova. */
export function NewVariable({
  label,
  onCreate,
  onCancel,
}: {
  label: string;
  onCreate: (name: string) => void;
  onCancel?: () => void;
}) {
  const { doc, update } = useBuilder();
  const [name, setName] = useState('');
  const id = useId();
  const clean = toVariableName(name);
  const problem = name ? variableNameProblem(clean, doc) : null;

  const create = () => {
    if (!clean || problem) return;
    update((d) => addVariable(d, clean));
    onCreate(clean);
  };

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-xs font-medium text-fg-subtle">
        {label} — nova variável
      </label>
      <div className="flex gap-2">
        <Input
          id={id}
          autoFocus
          value={name}
          maxLength={40}
          placeholder="ex.: area_de_atuacao"
          aria-invalid={problem ? true : undefined}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              create();
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              onCancel?.();
            }
          }}
        />
        <Button size="sm" className="h-10" onClick={create} disabled={!clean || !!problem}>
          <Plus aria-hidden className="size-4" />
          Criar
        </Button>
      </div>
      <p className={cn('text-xs', problem ? 'text-rose-600' : 'text-fg-subtle')}>
        {problem ?? (clean && clean !== name ? `Vai se chamar ${clean}` : 'Letras sem acento, números e _')}
        {onCancel && (
          <button type="button" onClick={onCancel} className="ml-2 underline hover:text-fg">
            cancelar
          </button>
        )}
      </p>
    </div>
  );
}

/** Várias variáveis (as do webhook, as de disponibilidade da visita): marcar e desmarcar. */
export function VariableChecklist({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string[];
  onChange: (names: string[]) => void;
  hint?: string;
}) {
  const { doc } = useBuilder();
  const names = [...doc.variables.map((v) => v.name), ...SYSTEM_VARIABLES.map((v) => v.name)];
  const extra = value.filter((v) => !names.includes(v));
  return (
    <fieldset className="space-y-1.5">
      <legend className="mb-1 text-xs font-medium text-fg-subtle">{label}</legend>
      <div className="flex flex-wrap gap-1.5">
        {[...names, ...extra].map((n) => {
          const on = value.includes(n);
          return (
            <button
              key={n}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(on ? value.filter((v) => v !== n) : [...value, n])}
              className={cn(
                'rounded-full px-2.5 py-1 font-mono text-xs transition-colors',
                on
                  ? 'bg-brand-400 text-on-brand'
                  : 'bg-surface text-fg-muted ring-1 ring-line-strong hover:text-fg',
                extra.includes(n) && 'line-through',
              )}
            >
              {n}
            </button>
          );
        })}
      </div>
      {hint && <p className="text-xs text-fg-subtle">{hint}</p>}
    </fieldset>
  );
}
